import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseVoiceRecorderResult {
  isRecording: boolean
  isSupported: boolean
  start: () => Promise<void>
  stop: () => Promise<File | null>
  cancel: () => void
}

/** Bevorzugtes Aufnahmeformat (Chrome/Edge/Firefox). */
const MIME_WEBM = 'audio/webm;codecs=opus'
/** Safari-Fallback: MediaRecorder kann dort kein webm — audio/mp4. */
const MIME_MP4 = 'audio/mp4'

/** Liefert das erste unterstuetzte MIME samt Dateiendung; '' = unbekannt. */
function pickMimeType(): { mime: string; ext: string } | null {
  if (typeof MediaRecorder === 'undefined') return null
  if (MediaRecorder.isTypeSupported(MIME_WEBM)) return { mime: MIME_WEBM, ext: 'webm' }
  // Safari: audio/mp4 (M4A-Container) — Dateiendung muss zum MIME passen.
  if (MediaRecorder.isTypeSupported(MIME_MP4)) return { mime: MIME_MP4, ext: 'm4a' }
  return null
}

export function useVoiceRecorder(): UseVoiceRecorderResult {
  const [isRecording, setIsRecording] = useState(false)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const stopResolverRef = useRef<((f: File | null) => void) | null>(null)
  // Unmount-Marker: ein noch laufendes getUserMedia darf nach dem Unmount
  // weder State setzen noch den Stream behalten (Leak/Permission-Hang).
  const mountedRef = useRef(true)
  // Aufnahme-Generation: stop/cancel/unmount invalidieren eine laufende
  // Aufnahme, damit ein spaeter onstop-Handler keinen stale File erzeugt.
  const sessionRef = useRef(0)

  const isSupported =
    typeof window !== 'undefined' && 'MediaRecorder' in window && !!navigator.mediaDevices?.getUserMedia

  const cleanup = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    recorderRef.current = null
    chunksRef.current = []
  }, [])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      // Unmount: laufende Aufnahme abbrechen, Stream-Tracks stoppen, Resolver
      // loesen (damit wartende stop()-Promises nicht haengen bleiben).
      mountedRef.current = false
      sessionRef.current += 1
      const resolver = stopResolverRef.current
      stopResolverRef.current = null
      if (recorderRef.current) {
        recorderRef.current.onstop = null
        recorderRef.current.ondataavailable = null
        try {
          if (recorderRef.current.state !== 'inactive') recorderRef.current.stop()
        } catch {
          /* ignore */
        }
      }
      cleanup()
      resolver?.(null)
      setIsRecording(false)
    }
  }, [cleanup])

  const start = useCallback(async () => {
    if (!isSupported || isRecording) return
    const picked = pickMimeType()
    if (!picked) {
      throw new Error('Audio-Aufnahme wird von diesem Browser nicht unterstützt.')
    }

    // Generation VOR dem await erfassen: cancel/unmount/erneuter Klick waehrend
    // des pending getUserMedia invalidieren diese Aufnahme, bevor sie beginnt.
    const generation = ++sessionRef.current
    // Doppelklick-Guard: zwei parallele start()-Aufrufe teilen sich die
    // Generation — nur die erste darf nach dem await weitermachen.
    const isCurrent = () => mountedRef.current && sessionRef.current === generation

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (err) {
      // Permission denied / NotFound / NotAllowed — State bleibt clean.
      throw err instanceof Error && err.message
        ? err
        : new Error('Mikrofon-Zugriff verweigert')
    }

    // getUserMedia war async — Component koennte unmounted/cancelled sein.
    if (!isCurrent()) {
      stream.getTracks().forEach((t) => t.stop())
      return
    }

    streamRef.current = stream
    let recorder: MediaRecorder
    try {
      recorder = new MediaRecorder(stream, { mimeType: picked.mime })
    } catch (err) {
      // Konstruktor-Fehler: Mikrofon-Track sofort freigeben (kein Leak).
      stream.getTracks().forEach((t) => t.stop())
      streamRef.current = null
      throw err instanceof Error && err.message
        ? err
        : new Error('Recorder konnte nicht starten')
    }
    recorderRef.current = recorder
    chunksRef.current = []

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) chunksRef.current.push(event.data)
    }
    recorder.onstop = () => {
      // Stale-Guard: nach cancel/unmount/neuer Session kein File erzeugen.
      if (sessionRef.current !== generation) {
        cleanup()
        return
      }
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || picked.mime })
      const file = new File([blob], `voice_${Date.now()}.${picked.ext}`, { type: blob.type })
      const resolver = stopResolverRef.current
      stopResolverRef.current = null
      cleanup()
      if (!mountedRef.current) {
        resolver?.(null)
        return
      }
      resolver?.(file.size > 0 ? file : null)
      setIsRecording(false)
    }
    try {
      recorder.start()
    } catch (err) {
      // start()-Fehler: Tracks freigeben, State resetten.
      recorderRef.current = null
      stream.getTracks().forEach((t) => t.stop())
      streamRef.current = null
      throw err instanceof Error && err.message
        ? err
        : new Error('Recorder konnte nicht starten')
    }
    setIsRecording(true)
  }, [isSupported, isRecording, cleanup])

  const stop = useCallback(async (): Promise<File | null> => {
    if (!recorderRef.current || !isRecording) return null
    // Doppelklick-Guard: laeuft bereits ein Stop, liefert der zweite Aufruf
    // null — Resolver nie ueberschreiben, Chunks nicht loeschen, kein
    // cleanup(). Nur der erste Aufrufer erhaelt den File (kein Doppel-Upload).
    if (stopResolverRef.current) return null
    return await new Promise<File | null>((resolve) => {
      stopResolverRef.current = resolve
      try {
        recorderRef.current?.stop()
      } catch {
        // Bereits inactive — Resolver direkt loesen, State resetten.
        stopResolverRef.current = null
        cleanup()
        setIsRecording(false)
        resolve(null)
      }
    })
  }, [isRecording, cleanup])

  const cancel = useCallback(() => {
    // Generation IMMER invalidieren — auch waehrend eines pending getUserMedia
    // (noch kein Recorder vorhanden): der start()-Guard stoppt dann den Stream.
    sessionRef.current += 1
    if (recorderRef.current) {
      recorderRef.current.onstop = null
      recorderRef.current.ondataavailable = null
      try {
        if (recorderRef.current.state !== 'inactive') recorderRef.current.stop()
      } catch {
        /* ignore */
      }
    }
    const resolver = stopResolverRef.current
    stopResolverRef.current = null
    cleanup()
    resolver?.(null)
    setIsRecording(false)
  }, [cleanup])

  return { isRecording, isSupported, start, stop, cancel }
}
